/**
 * Client-side port of the backend quantity-offer pricing engine + money formatter (feature 0026-07):
 *   aquilixshop-back .../product/internal/domain/QuantityOffer.kt#QuantityOfferEngine  (money math)
 *   aquilixshop-back .../product/internal/application/QuantityOfferPricing.kt           (formatMoney + projection)
 *
 * Used ONLY to recompute an offer tile's pack-price labels AFTER the buyer switches variant client-side. The
 * FIRST paint always uses the backend's pre-formatted strings VERBATIM (byte-identical SSR/hydration); this
 * port kicks in only once a NON-default variant is selected, so there is never a hydration mismatch. The two
 * implementations MUST produce the identical string for the same (cents, currency) — the unit tests pin the
 * shared vectors. Money is integer cents; there is deliberately NO Intl here (the backend formats without a
 * locale, so both sides print identical separators/affixes).
 */
import { formatMoney, isArabicLocale, minorUnitExponent } from '@aquilixshop/money';
/**
 * `round(baseUnit * (100 - percent) / 100)` — mirrors QuantityOfferEngine.percentUnit (BigDecimal HALF_UP).
 * JS `Math.round` is HALF_UP for the non-negative operands here, so it matches the backend cent-for-cent.
 */
function percentUnit(baseUnit, percent) {
    const p = Math.min(100, Math.max(0, percent));
    return Math.round((baseUnit * (100 - p)) / 100);
}
/** `round(packTotal / minQty)` — the per-unit price of a fixed-price pack (HALF_UP, minQty ≥ 1). */
function fixedPackUnit(packTotal, minQty) {
    const m = Math.max(1, minQty);
    return Math.round(packTotal / m);
}
/**
 * The discounted per-unit price (cents) for [tile] against [baseUnit] — mirrors
 * QuantityOfferEngine.tierUnitPrice. FIXED_AMOUNT / FIXED_PRICE apply ONLY when [currencyMatches] (their value
 * is money in the offer currency); PERCENT ignores currency. The result is clamped to `[0, baseUnit]`, so an
 * offer can never raise a price or go negative.
 */
export function tierUnitPrice(baseUnit, tile, currencyMatches) {
    let raw;
    switch (tile.kind) {
        case 'PERCENT':
            raw = percentUnit(baseUnit, tile.value);
            break;
        case 'FIXED_AMOUNT':
            raw = currencyMatches ? baseUnit - tile.value : baseUnit;
            break;
        case 'FIXED_PRICE':
            raw = currencyMatches ? fixedPackUnit(tile.value, tile.qty) : baseUnit;
            break;
        default:
            raw = baseUnit;
    }
    return Math.min(baseUnit, Math.max(0, raw));
}
/** Group the integer part in thousands with [separator] (e.g. 1000 → "1 000"). Ports the Kotlin `group`. */
function group(value, separator) {
    const digits = String(value);
    const firstGroup = digits.length % 3;
    let sb = '';
    for (let i = 0; i < digits.length; i += 1) {
        if (i !== 0 && (i - firstGroup) % 3 === 0)
            sb += separator;
        sb += digits[i];
    }
    return sb;
}
/**
 * Deterministic integer-cents money formatting — a byte-for-byte port of QuantityOfferPricing.formatMoney (NO
 * Intl/locale so Node SSR and the browser print the identical string). MAD `1 500,00 dh`, EUR `1 500,00 €`,
 * USD `$1,500.00`, GBP `£1,500.00`; any other currency → `1 500.00 XXX`.
 *
 * ── [locale] : UNE BOUTIQUE ARABE ÉCRIT `899,00 د.م.`, PAS `899,00 dh` ────────────────────────────────
 *
 * Fondatrice, 23/10/2026, capture du formulaire COD d'une boutique arabe : « nrmlm ça doit etre comme ça
 * 899,00 د.م. ». Le reste de sa page l'écrivait DÉJÀ ainsi : le prix sous le titre passe par
 * `@aquilixshop/money`, qui demande le symbole à `Intl` dans la langue de la boutique et n'impose que les
 * CHIFFRES à la française. Seul ce formateur-ci, copie exacte du back, gardait `dh` en dur — d'où deux
 * devises différentes sur un même écran.
 *
 * La branche arabe délègue donc au même formateur que le prix produit : un seul endroit décide du symbole.
 * Sans [locale] (ou hors arabe) la chaîne reste identique au byte près à celle du back — les paliers
 * d'offre, eux, sont pré-formatés côté serveur et doivent continuer de correspondre.
 */
export function formatOfferMoney(cents, currency, locale) {
    // Le SSR Node et le navigateur appellent tous deux cette branche avec la même locale, donc la même
    // chaîne des deux côtés : pas d'écart d'hydratation (voir OfferTiles, qui recalcule ses paliers en
    // arabe au lieu de peindre le libellé « dh » du serveur).
    if (isArabicLocale(locale))
        return formatMoney({ amount: cents, currency }, locale);
    // Feature 0049 lot D: the exponent comes from ISO (@aquilixshop/money), not a hard-coded 100 — kept in
    // lockstep with the backend's QuantityOfferPricing.formatMoney, which does exactly this. `minor` is EMPTY
    // for a zero-decimal currency, so the separator is applied through dot()/comma() rather than inline:
    // "2 500.00 XOF" would invent a centime the franc CFA does not have.
    const exponent = minorUnitExponent(currency);
    const factor = 10 ** exponent;
    const negative = cents < 0;
    const absCents = Math.abs(cents);
    const major = Math.floor(absCents / factor);
    const minor = exponent === 0 ? '' : String(absCents % factor).padStart(exponent, '0');
    const dot = minor === '' ? '' : `.${minor}`;
    const comma = minor === '' ? '' : `,${minor}`;
    const sign = negative ? '-' : '';
    switch (currency.toUpperCase()) {
        case 'USD':
            return `${sign}$${group(major, ',')}${dot}`;
        case 'GBP':
            return `${sign}£${group(major, ',')}${dot}`;
        case 'EUR':
            return `${sign}${group(major, ' ')}${comma} €`;
        case 'MAD':
            return `${sign}${group(major, ' ')}${comma} dh`;
        default:
            // Comma, not dot — found by the 0049 lot G acceptance run. The dot was only ever right for the two
            // anglophone branches above; as the FALLBACK it gave a DZD or TND store "1 500.00 DZD" while the MAD
            // store beside it correctly read "1 500,00 dh". Same French-writing markets, so the default was wrong.
            // Kept in lockstep with the backend's QuantityOfferPricing.formatMoney, which made the same change.
            return `${sign}${group(major, ' ')}${comma} ${currency}`;
    }
}
/**
 * Recompute a tile's pack labels from a variant's unit price — mirrors buildActiveOfferProjection so a render
 * AFTER a variant switch matches what the backend would have produced for that variant. `currencyMatches` is
 * derived from [offerCurrency] vs [variantCurrency] (NOT hard-defaulted true): a FIXED tile applies ONLY when the
 * selected variant's currency matches the offer's — on a mismatch it shows NO discount, exactly as the checkout
 * engine would charge, so the display never promises a discount the backend won't honor. [offerCurrency] defaults
 * to [variantCurrency] (the in-practice case — all of a product's variants share the store/offer currency), so a
 * 3-arg call keeps the previous match-always behaviour. Labels are formatted in the variant's currency (what the
 * buyer pays).
 */
export function computeOfferTileLabels(tile, variantUnit, variantCurrency, offerCurrency = variantCurrency, locale) {
    // Every unit of the pack is this one variant: the pack is `qty` copies of its price.
    return computeOfferPackLabels(tile, Array.from({ length: Math.max(1, tile.qty) }, () => variantUnit), variantCurrency, offerCurrency, locale);
}
/**
 * The pack labels for a tile whose units are NOT all the same variant (28/09/2026 — the variants gap: a
 * pack of three can be one Medium and two Large, each at its own list price). Mirrors what the checkout
 * engine charges for that basket: the tier rule is applied to EACH unit's own base price and the pack is
 * the sum (`applyQuantityOffers` prices each cart line from its own `unitPriceAmount` with the tier chosen
 * on the product's TOTAL quantity), so the number on the tile is the number on the order.
 *
 * [unitCents] is one entry per unit in the pack (its variant's list price). The struck «was» is the sum
 * of the list prices, the saving their difference. The `unit` template's per-unit label is printed only
 * when every unit lands on the SAME discounted price — a pack that mixes two list prices has no honest
 * single «per unit» figure, so the label is left empty and the tile falls back to the pack total.
 */
export function computeOfferPackLabels(tile, unitCents, variantCurrency, offerCurrency = variantCurrency, 
/** La langue de la boutique — en arabe le symbole devient `د.م.` (voir {@link formatOfferMoney}). */
locale) {
    const currencyMatches = variantCurrency.toUpperCase() === offerCurrency.toUpperCase();
    let packBase = 0;
    let packPrice = 0;
    let firstUnit = null;
    let sameUnit = true;
    for (const base of unitCents) {
        const newUnit = tierUnitPrice(base, tile, currencyMatches);
        packBase += base;
        packPrice += newUnit;
        if (firstUnit === null)
            firstUnit = newUnit;
        else if (newUnit !== firstUnit)
            sameUnit = false;
    }
    const hasDiscount = packPrice < packBase;
    return {
        priceLabel: formatOfferMoney(packPrice, variantCurrency, locale),
        compareLabel: tile.hideComparison || !hasDiscount
            ? null
            : formatOfferMoney(packBase, variantCurrency, locale),
        saveLabel: formatOfferMoney(packBase - packPrice, variantCurrency, locale),
        unitPriceLabel: sameUnit && firstUnit !== null ? formatOfferMoney(firstUnit, variantCurrency, locale) : '',
    };
}
/**
 * Build a render-ready {@link ProductActiveOffer} from a RAW offer definition + the product's DEFAULT-variant
 * unit price (cents) + that variant's currency. Each tier's pack labels are computed via
 * {@link computeOfferTileLabels} — the SAME math the storefront/backend use — so a builder-preview tile reads
 * identically to the live storefront (single source of truth). The offer's own currency drives the FIXED-tile
 * discount gating (a currency mismatch shows no discount, mirroring the checkout engine + the quantity block).
 */
export function buildActiveOfferFromOffer(offer, defaultVariantUnitCents, variantCurrency) {
    const tiers = offer.tiers.map((t) => {
        const base = {
            tierId: t.id,
            qty: t.minQty,
            kind: t.kind,
            value: t.value,
            label: t.label,
            badge: t.badge,
            preselect: t.preselect,
            hideComparison: t.hideComparison,
            plaqueText: t.plaqueText,
            plaqueTextColor: t.plaqueTextColor,
            plaqueBgColor: t.plaqueBgColor,
            priceColor: t.priceColor,
            bgColor: t.bgColor,
            borderColor: t.borderColor,
            // Placeholders — overwritten below by the computed labels (the offer currency gates FIXED tiles).
            priceLabel: '',
            compareLabel: null,
            saveLabel: '',
            unitPriceLabel: '',
        };
        const labels = computeOfferTileLabels(base, defaultVariantUnitCents, variantCurrency, offer.currency);
        return { ...base, ...labels };
    });
    return {
        offerId: offer.id,
        productId: offer.productId,
        currency: offer.currency,
        title: offer.title,
        imageUrl: offer.imageUrl ?? null,
        template: offer.template ?? 'classic',
        unitImageMode: offer.unitImageMode ?? 'variant',
        unitImages: offer.unitImages ?? [],
        tiers,
    };
}
//# sourceMappingURL=offerPricing.js.map